import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileBarChart, Search, PlayCircle, ShieldAlert, RefreshCcw, Clock,
  CheckCircle2, XCircle, Loader2, ExternalLink, Calendar, Users,
  Settings2, Save,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const CATEGORY_COLOURS = {
  executive:  "bg-violet-100 text-violet-800",
  finance:    "bg-emerald-100 text-emerald-800",
  operations: "bg-sky-100 text-sky-800",
  hseq:       "bg-rose-100 text-rose-800",
  vehicles:   "bg-amber-100 text-amber-800",
  contracts:  "bg-indigo-100 text-indigo-800",
  hr:         "bg-teal-100 text-teal-800",
  other:      "bg-slate-100 text-slate-700",
};

const FORMAT_LABEL = {
  pdf: "PDF", excel: "Excel", "email+pdf": "Email + PDF", csv: "CSV",
};

const CADENCE_LABEL = (r) => {
  if (!r?.cadence) return "Manual";
  const parts = [r.cadence];
  if (r.cadence_day) parts.push(r.cadence_day);
  if (r.cadence_time) parts.push(r.cadence_time);
  return parts.join(" · ");
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

// ============================================================
// Reports page
// ============================================================
export default function ReportsPage() {
  const { can, loaded } = usePermissions();
  const canView = can("reports", "view");
  const canExport = can("reports", "export");
  const canEdit = can("reports", "edit");

  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setRefreshing(true);
    try {
      const [{ data: list }, { data: s }, { data: h }] = await Promise.all([
        api.get(`/reports${category ? `?category=${category}` : ""}`),
        api.get("/reports/stats"),
        api.get("/reports/runs/history?limit=50"),
      ]);
      setReports(list);
      setStats(s);
      setHistory(h);
    } catch (e) {
      toast.error("Reports failed to load", { description: formatApiErrorDetail(e) });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, [canView, category]);

  const filtered = useMemo(() => {
    if (!search) return reports;
    const q = search.toLowerCase();
    return reports.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q) ||
      (r.report_id || "").toLowerCase().includes(q)
    );
  }, [search, reports]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((r) => {
      const cat = r.category || "other";
      (g[cat] = g[cat] || []).push(r);
    });
    return g;
  }, [filtered]);

  const runReport = async (r) => {
    if (!r.endpoint) {
      toast.info("This report has no run endpoint yet.");
      return;
    }
    setRunning(r.report_id);
    const start = Date.now();
    try {
      if (r.format === "pdf" && r.endpoint.endsWith(".pdf")) {
        // Open pdf endpoint in a new tab
        const url = process.env.REACT_APP_BACKEND_URL + "/api" + r.endpoint;
        window.open(url, "_blank");
      } else if (r.run_action === "download") {
        // Try to fetch the endpoint — will hit the API's own guard if not implemented
        try {
          await api.get(r.endpoint);
          toast.success(`Ran ${r.name}`);
        } catch (e) {
          toast.info(`${r.name}: endpoint responded ${e?.response?.status || "error"}. See run history.`);
        }
      } else if (r.run_action === "email") {
        await api.post(`${r.endpoint.replace("/preview", "/send")}`);
        toast.success(`${r.name} queued`);
      }
      // Record the run
      await api.post(`/reports/${r.report_id}/runs`, {
        report_id: r.report_id,
        status: "success",
        duration_ms: Date.now() - start,
        output_url: r.endpoint,
      });
    } catch (e) {
      await api.post(`/reports/${r.report_id}/runs`, {
        report_id: r.report_id,
        status: "failed",
        duration_ms: Date.now() - start,
        error: e?.message?.slice(0, 200),
      }).catch(() => {});
      toast.error(`${r.name} failed`, { description: formatApiErrorDetail(e) });
    } finally {
      setRunning(null);
      load();
    }
  };

  if (!loaded) return <AppLayout><div className="p-8">Loading…</div></AppLayout>;
  if (!canView) {
    return (
      <AppLayout>
        <div className="max-w-md mx-auto mt-24 rounded-lg border border-slate-200 bg-white p-6 text-center">
          <ShieldAlert className="w-8 h-8 mx-auto text-rose-500 mb-2" />
          <div className="font-semibold text-slate-800 mb-1">Restricted</div>
          <p className="text-sm text-slate-600">Your role does not have access to the Reports library.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="reports-page" className="p-6 max-w-[1400px] mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display text-slate-900 flex items-center gap-2">
              <FileBarChart className="w-7 h-7 text-gbe" />
              Reports
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Central library of scheduled &amp; on-demand reports. Section 16 of the scope.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        {/* Stat cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Total reports" value={stats.total_reports} />
            <Stat label="Live" value={stats.live_reports} tone="text-emerald-700" />
            <Stat label="Runs (7d)" value={stats.runs_last_7d} />
            <Stat label="Failed (7d)" value={stats.failed_last_7d}
                  tone={stats.failed_last_7d > 0 ? "text-rose-700" : "text-slate-700"} />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search reports…" className="pl-8" />
          </div>
          <Select value={category || "__all"} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All categories</SelectItem>
              {Object.keys(CATEGORY_COLOURS).map((c) => (
                <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Grouped report cards */}
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading reports…</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="p-8 text-center text-slate-500">No reports match your filters.</div>
        ) : (
          Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500">{cat}</div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {list.map((r) => (
                  <ReportCard
                    key={r.report_id}
                    report={r}
                    onRun={runReport}
                    running={running === r.report_id}
                    canRun={canExport}
                    canEdit={canEdit}
                    onSchedule={() => setScheduleTarget(r)}
                  />
                ))}
              </div>
            </div>
          ))
        )}

        {/* Run history */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-slate-600 text-xs uppercase tracking-wider flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" /> Recent run history
          </div>
          {history.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">No runs recorded yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Report</th>
                  <th className="text-left px-3 py-2 font-medium">Started</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Duration</th>
                  <th className="text-left px-3 py-2 font-medium">Run by</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={TID.reportsHistoryRow(h.id)} className="border-t">
                    <td className="px-3 py-2 font-medium text-slate-800">{h.report_id}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{fmtDateTime(h.started_at)}</td>
                    <td className="px-3 py-2">
                      {h.status === "success" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5" />success
                        </span>
                      ) : h.status === "failed" ? (
                        <span className="inline-flex items-center gap-1 text-rose-700 text-xs">
                          <XCircle className="w-3.5 h-3.5" />failed
                        </span>
                      ) : (
                        <span className="text-xs text-amber-700">in progress</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600 text-xs">
                      {h.duration_ms != null ? `${h.duration_ms} ms` : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{h.run_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ScheduleDialog
        report={scheduleTarget}
        open={!!scheduleTarget}
        onOpenChange={(v) => { if (!v) setScheduleTarget(null); }}
        onSaved={load}
      />
    </AppLayout>
  );
}


function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-2xl mt-1 ${tone || "text-slate-900"}`}>
        {value == null ? "—" : value.toLocaleString()}
      </div>
    </div>
  );
}


function ReportCard({ report, onRun, running, canRun, canEdit, onSchedule }) {
  const catColour = CATEGORY_COLOURS[report.category] || CATEGORY_COLOURS.other;
  const latest = report.latest_run;
  const schedEnabled = report.schedule?.enabled;
  return (
    <div data-testid={TID.reportsRow(report.report_id)}
         className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-3 hover:border-gbe transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-slate-800">{report.name}</div>
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{report.description}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${catColour}`}>
            {report.category}
          </Badge>
          {schedEnabled && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
              <Clock className="w-3 h-3 mr-1" />
              Scheduled
            </Badge>
          )}
        </div>
      </div>
      <div className="text-xs text-slate-600 space-y-1">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-slate-400" /> {CADENCE_LABEL(report)}
        </div>
        <div className="flex items-center gap-1.5">
          <Users className="w-3 h-3 text-slate-400" />
          {(report.audience || []).join(", ") || "all users"}
        </div>
        <div className="text-slate-500">
          Format: <span className="font-medium text-slate-700">{FORMAT_LABEL[report.format] || report.format || "—"}</span>
          {report.status === "planned" && (
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 bg-slate-100 text-slate-600 border-slate-200">
              Planned
            </Badge>
          )}
        </div>
        {latest && (
          <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">
            Last run: <span className={latest.status === "success" ? "text-emerald-700" : "text-rose-700"}>
              {latest.status}
            </span> — {fmtDateTime(latest.started_at)}
            {latest.scheduled && <span className="ml-1 text-slate-400">(scheduled)</span>}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        {canEdit && (
          <Button size="sm" variant="ghost"
                  data-testid={`reports-schedule-${report.report_id}`}
                  onClick={() => onSchedule?.()}>
            <Settings2 className="w-3.5 h-3.5 mr-1" />Schedule
          </Button>
        )}
        <Button size="sm" variant="ghost"
                onClick={() => window.open(process.env.REACT_APP_BACKEND_URL + "/api" + (report.endpoint || ""), "_blank")}
                disabled={!report.endpoint}>
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Endpoint
        </Button>
        <Button size="sm" onClick={() => onRun(report)}
                disabled={!canRun || running || report.status === "planned"}
                data-testid={TID.reportsRun(report.report_id)}
                className="bg-gbe hover:bg-gbe/90 text-white">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                     <><PlayCircle className="w-3.5 h-3.5 mr-1" />Run</>}
        </Button>
      </div>
    </div>
  );
}


// ============================================================
// Schedule dialog
// ============================================================
const DOW_OPTIONS = [
  { value: "mon", label: "Monday" },
  { value: "tue", label: "Tuesday" },
  { value: "wed", label: "Wednesday" },
  { value: "thu", label: "Thursday" },
  { value: "fri", label: "Friday" },
  { value: "sat", label: "Saturday" },
  { value: "sun", label: "Sunday" },
];

function ScheduleDialog({ report, open, onOpenChange, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [dayOfWeek, setDayOfWeek] = useState("mon");
  const [dayOfMonth, setDayOfMonth] = useState("");
  const [hour, setHour] = useState(6);
  const [minute, setMinute] = useState(0);
  const [recipients, setRecipients] = useState("");
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  useEffect(() => {
    if (!report) return;
    const s = report.schedule || {};
    setEnabled(!!s.enabled);
    setDayOfWeek(s.cron?.day_of_week || "mon");
    setDayOfMonth(s.cron?.day || "");
    setHour(s.cron?.hour ?? 6);
    setMinute(s.cron?.minute ?? 0);
    setRecipients((s.recipients || []).join(", "));
  }, [report]);

  if (!report) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/reports/${report.report_id}/schedule`, {
        enabled,
        day_of_week: dayOfMonth ? null : dayOfWeek,
        day: dayOfMonth || null,
        hour: Number(hour) || 0,
        minute: Number(minute) || 0,
        recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(enabled ? "Schedule enabled" : "Schedule disabled");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: formatApiErrorDetail(e) });
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      await api.post(`/reports/${report.report_id}/run-now`);
      toast.success("Report queued");
      onSaved?.();
    } catch (e) {
      toast.error("Run failed", { description: formatApiErrorDetail(e) });
    } finally { setRunningNow(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-gbe" />
            Schedule report
          </DialogTitle>
          <DialogDescription>{report.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled}
                   onChange={(e) => setEnabled(e.target.checked)}
                   data-testid="reports-schedule-enabled" />
            Enable scheduled runs
          </label>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Day of week</label>
              <Select value={dayOfWeek} onValueChange={setDayOfWeek}
                      disabled={!!dayOfMonth}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOW_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">— or day of month</label>
              <Input type="number" min="1" max="28"
                     value={dayOfMonth}
                     onChange={(e) => setDayOfMonth(e.target.value)}
                     placeholder="e.g. 1" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Hour (UTC)</label>
              <Input type="number" min="0" max="23" value={hour}
                     onChange={(e) => setHour(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Minute</label>
              <Input type="number" min="0" max="59" value={minute}
                     onChange={(e) => setMinute(e.target.value)} />
            </div>
          </div>

          {report.run_action === "email" && (
            <div>
              <label className="text-xs text-slate-500">
                Recipients (comma-separated)
              </label>
              <Textarea rows={2} value={recipients}
                        onChange={(e) => setRecipients(e.target.value)}
                        placeholder="darren@gbelectrical.com.au, ops@gbegroup.com" />
              <div className="text-[11px] text-slate-500 mt-1">
                Delivered via Resend when the schedule fires. Every send is logged to Audit Trail.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="justify-between">
          <Button variant="outline" onClick={runNow} disabled={runningNow}>
            {runningNow ? <Loader2 className="w-4 h-4 animate-spin" /> :
                          <><PlayCircle className="w-4 h-4 mr-2" />Run now</>}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}
                    data-testid="reports-schedule-save"
                    className="bg-gbe hover:bg-gbe/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> :
                        <><Save className="w-4 h-4 mr-2" />Save</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
